interface ErrorReport {
  message: string
  stack?: string
  componentStack?: string
  page?: string
  userId?: string
  sessionId?: string
  buildVersion?: string
  environment?: string
}

class ErrorReporter {
  private apiUrl: string
  private sessionId: string

  constructor() {
    this.apiUrl = process.env.NEXT_PUBLIC_API_URL || 'https://api.socialloop.app'
    this.sessionId = this.generateSessionId()
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  async reportError(error: ErrorReport): Promise<void> {
    try {
      // Only report errors in production or when explicitly enabled
      if (process.env.NODE_ENV !== 'production' && !process.env.NEXT_PUBLIC_ENABLE_ERROR_REPORTING) {
        console.log('Error reporting disabled in development')
        return
      }

      const payload = {
        ...error,
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
        page: typeof window !== 'undefined' ? window.location.pathname : error.page,
        environment: process.env.NODE_ENV || 'production',
        buildVersion: process.env.NEXT_PUBLIC_BUILD_VERSION || '1.0.0'
      }

      // TODO Stage 7: wire to Sentry/Convex — legacy Express monitoring endpoint removed
      console.error('[error-report]', payload)
    } catch (reportingError) {
      // Silently fail error reporting to avoid infinite loops
      console.warn('Error reporting failed:', reportingError)
    }
  }

  setUserId(userId: string): void {
    // Store user ID for future error reports
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('error-reporter-user-id', userId)
    }
  }

  private getUserId(): string | undefined {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('error-reporter-user-id') || undefined
    }
    return undefined
  }
}

export const errorReporter = new ErrorReporter()

// Convenience function for reporting errors
export const reportError = (error: Error, componentStack?: string, additionalContext?: Partial<ErrorReport>) => {
  errorReporter.reportError({
    message: error.message,
    stack: error.stack,
    componentStack,
    userId: errorReporter['getUserId'](),
    ...additionalContext
  })
}